import { createGlobalStyle } from "styled-components";

import {
  bp,
  flexColumn,
  flexSpBetween,
  media,
  rem,
  size,
} from "./Utilities.styled";

import "@radix-ui/colors/mauve.css";
import "@radix-ui/colors/violet.css";
import "@radix-ui/colors/blue.css";
import "@radix-ui/colors/green.css";
import "@radix-ui/colors/orange.css";
import "@radix-ui/colors/yellow.css";
import "@radix-ui/colors/pink.css";
import "@radix-ui/colors/brown.css";
import "@radix-ui/colors/black-alpha.css";
import "@radix-ui/colors/red.css";
import "@radix-ui/colors/purple.css";
import "@radix-ui/colors/crimson.css";
import "@radix-ui/colors/lime.css";


export const GlobalStyles = createGlobalStyle`

    :root {
        font-synthesis: none;
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        -webkit-text-size-adjust: 100%;

        // CSS variables ----------------------------------------
        --clr-primary-100: hsl(253, 100%, 61%, 0.1);
        --clr-primary-200: hsl(253, 100%, 61%, 0.3);
        --clr-primary-400: 	hsl(253, 100%, 61%);
        --clr-primary-700: 	hsl(242, 98%, 16%);

        --clr-notification: hsl(10, 100%, 61%);
        --clr-notification-100: hsl(10, 100%, 61%, 0.3);

        --clr-accent: hsl(180, 96%, 41%);
        --clr-accent-100: hsl(180, 96%, 41%, 0.1);
        --clr-accent-disabled: hsl(208, 11%, 75%);
        --clr-rose: hsl(335, 95%, 45%);
        --clr-yellow: hsl(44, 100%, 48%);

        --clr-white: hsl(0, 0%, 100%);
        --clr-black: hsl(0, 0%, 0%);

        --clr-gray-100: hsl(200, 20%, 97%);
        --clr-gray-150: hsl(0, 0%, 89%);
        --clr-gray-175: hsl(0, 0%, 85%);
        --clr-gray-200: hsl(0, 0%, 80%);
        --clr-gray-300: hsl(0, 0%, 70%);
        --clr-gray-400: hsl(0, 0%, 50%);
        --clr-gray-500: hsl(0, 0%, 40%);
        --clr-gray-600: hsl(0, 0%, 30%);
        --clr-gray-700: hsl(0, 0%, 12%);

        --font-primary: "Poppins", sans-serif;
        --font-secondary: "Noto Sans Mono", monospace;

        --ease-slow-start: cubic-bezier(0.52, 0.01, 0, 1);
        --ease-quick-start-and-end: cubic-bezier(0.19, 1, 0.22, 1);
        --ease-almost-linear: cubic-bezier(0.4,0.6,0.6,1);
        --ease-text-reveal: cubic-bezier(0.77, 0, 0.175, 1);

        --border-radius-1: 3px;
        --border-radius-2: 5px;
        --border-radius-3: 7px;
        --border-radius-4: 10px;
        --border-radius-5: 15px;


        // APP-SPECIFIC CSS variables ----------------------------------------
        --footer-height: ${rem(42)};
        --sidebar-collapsed-width: ${rem(70)};
        --sidebar-full-width: ${rem(230)};
        --sidebar-width: var(--sidebar-collapsed-width);
        --patient-navbar-height: ${rem(114)};
        --dashboard-navbar-height: ${rem(102)};
        --network-notification-height: ${rem(32)};
        --network-notification-dismiss-delay: 2s;
        --network-notification-transition-duration: 0.35s;

        ${media(bp.large1)}{
            --sidebar-width: var(--sidebar-full-width);
        }
    }

    *, ::before, ::after {
        padding: 0;
        margin: 0;
        box-sizing: border-box;
        font-family: inherit;
    }

    html {
        scroll-behavior: smooth; // for faster scroll to top during page transitions
        /* height: 100%; */ // Lenis needs this to be auto
    }

    body {
        min-height: 100%;
        color: var(--clr-primary-700);
        background-color: var(--clr-gray-100);
        font-family: var(--font-primary);
        font-size: ${rem(16)};
        line-height: 1.5;
        overflow-x: hidden;
        pointer-events: all !important; //* Radix UI makes this none when dialogs are open, but it also prevents Mantine's multiselect from working
    }

    h1, h2 {
        line-height: 1.1;
    }

    h3, h4, h5, h6 {
        line-height: 1.2;
    }

    img, iframe {
        display: block;
        ${size("100%")}
        max-width: 100%;
        object-fit: cover;
        object-position: center;
        border-style: none;  // Remove the border on images inside links in IE 10.
    }

    ul, ol {
        list-style-type: none;
    }

    a {
        color: inherit;
        display: inline-block;
        text-decoration: none;

        &.hover-underline {
          &:hover {
            text-decoration: underline !important;
          }
        }
    }

    button, input, select, textarea {
        border: none;
    }

    button {
        cursor: pointer;
        background: none;
    }

    label {
        font-size: ${rem(14)};
        font-weight: 500;
        /* line-height: 1.1; */
        margin-bottom: 0.3rem;

        sup:not(.no-color) {
            color: var(--clr-rose);
        }
    }
    
    input,
    textarea{
        width: 100%;
        padding: 0.6em 1em;
        color: var(--clr-primary-700) !important;
        border-radius: var(--border-radius-2);
        border: 1px solid rgb(0,0,0,0);
        border-color: var(--clr-gray-200) !important;
        outline: none;
        font-size: ${rem(13)} !important;
        
        &:disabled {
            background-color: var(--clr-gray-100);
            border: 1px solid var(--clr-gray-150);
            color: var(--clr-gray-500) !important;
            cursor: not-allowed;
        }
        
        &:focus:not(:disabled):not(.mantine-TagsInput-inputField),
        &:focus-within:not(:disabled):not(.mantine-TagsInput-inputField) {
            box-shadow: inset 0 0 0 1px var(--clr-primary-400) !important;
            border-color: rgb(0,0,0,0) !important;
            outline: none !important;
        }
    }

    .mantine-TagsInput-input, .mantine-InputBase-input {
      &:focus-within {
        border-color: var(--clr-primary-400);
      }
    }

    // todo: Remove this when all inputs are using Mantine
    .mantine-date-picker-input,
    .mantine-date-time-picker {
      width: 100%;
      color: var(--clr-primary-700) !important;
      border-radius: var(--border-radius-2);
      border: 1px solid rgb(0,0,0,0);
      border-color: var(--clr-gray-200) !important;
      outline: none;
      font-size: ${rem(13)} !important;

      /* &:focus:not(:disabled),
        &:focus-within:not(:disabled) {
          box-shadow: inset 0 0 0 1px var(--clr-primary-400) !important;
          border-color: rgb(0,0,0,0) !important;
          outline: none !important;
      } */
    }
    
    textarea {
        line-height: 1.2;
        resize: vertical;
    }

    tbody {
    tr {

      .last-updated,
      .table-controls {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 1rem;
      }

      .last-updated {
        @media screen and (pointer: coarse) {
          display: none;
        }
      }

      @media screen and (pointer: fine) {
        &:hover {
          .table-controls {
            opacity: 1;
            z-index: 1;
            pointer-events: all;
          }

          .last-updated {
            opacity: 0;
            pointer-events: none;
            z-index: -1;
          }
        }

        .table-controls {
          opacity: 0;
          z-index: -1;
          pointer-events: none;

          &:focus-within {
            opacity: 1;
            z-index: 1;
            pointer-events: all;

            & + .last-updated {
              opacity: 0;
            }
          }
        }
      }
    }
  }

    .form {
    ${flexColumn}
    gap: 1rem;
    margin-top: 2rem;

    &-field {
      ${flexColumn}

      &:has(.description) label {
        margin-bottom: 0;
      }

      .description {
        font-size: ${rem(12)};
        color: var(--clr-gray-400);
        line-height: 1.2;
        margin-bottom: 0.3rem;
      }

      &.error {

        input {
            border-color: var(--clr-rose);

            &::placeholder {
                color: var(--clr-rose);
            }
        }
      }

      .error-message {
        margin-top: 0.25em;
        font-size: ${rem(12)};
        color: var(--clr-rose);
        line-height: 1.2;
      }
    }

    &-group {
      ${flexSpBetween}
      align-items: start;
      gap: 1.5rem;

      & > div {
        flex-basis: 50%;
      }
    }
  }

  /* Mantine custom styles ----------------------------------- */

  /* .mantine-input, .mantine-input-wrapper {
    border: 1px solid var(--mantine-color-violet-filled);
    
    &:focus-within, &:focus {
        border: 1px solid var(--mantine-color-red);
    }
  } */
  .mantine-text-input-wrapper {
    &:has(p) label {
        margin-bottom: 0;
    }
  }

  .mantine-password-input {
    &:focus-within {
        border: none;
    }
  }

  .mantine-button {
      font-weight: 500;
  }

  .mantine-tab {
    &[data-active] {
        font-weight: 500;
        color: var(--mantine-color-primary-6);
    }
  }

  .mantine-PillsInput-input {
    display: flex; // This fixes input placeholder not appearing vertically centered
  }

  .mantine-Badge-label {
    /* line-height: unset !important; */
    /* height: auto !important; */
    text-transform: capitalize;
    font-weight: 500;
  }

  .mantine-DatePicker-day,
  .mantine-DatePickerInput-day,
  .mantine-DateTimePicker-day {
    &[data-selected]{
      background-color:var(--mantine-color-accent-4);
      color: var(--mantine-color-white);
    }
    &[data-in-range]:not(&[data-first-in-range]):not(&[data-last-in-range]) {
      background-color:var(--mantine-color-accent-0);
      color: var(--mantine-color-black);
    }
  }

  .mantine-Alert-body {
    text-align: left;
    gap: 0.125rem;
  }

  .mantine-notification {
  --notification-color: var(--mantine-color-blue-6);
  }

    .mantine-Tooltip-tooltip {
      font-size: ${rem(12)}
    }


    // Remove all animations and transitions for users
    // who have it turned off in system settings
    @media (prefers-reduced-motion: reduce) {
    *,
    ::before,
    ::after {
        animation-delay: -1ms !important;
        animation-duration: 1ms !important;
        animation-iteration-count: 1 !important;
        background-attachment: initial !important;
        scroll-behavior: auto !important;
        transition-duration: 0s !important;
        transition-delay: 0s !important;
    }
}
`;
